import { ApiResult } from '@src/api/apiClient/apiResult';
import { axiosClient } from '@src/api/apiClient/axiosClient';
import { Event, EventErrorResponse, EventForm } from '@src/api/types/event';
import { Pagination } from '@src/api/types/pagination';
import { IndexRequest } from '@src/api/types/shared/request';

type EventIndexResponse = {
  data: Event[];
  pagination: Pagination;
};
type EventCreateUpdateResponse = Event;
type EventCreateErrorResponse = {
  event: EventErrorResponse;
  message: string;
};
type EventUpdateErrorResponse = {
  event: EventErrorResponse;
  message: string;
};

type EventPreviewResponse = {
  id: number;
  event_id: number;
  uuid: string;
};
type EventPreviewErrorResponse = EventErrorResponse;

export type EventUploadFileResponse = {
  url: string;
};

type EventUploadFileErrorResponse = {
  file: string[];
};

export type EventDeleteFileResponse = unknown;
export type EventDeleteFileErrorResponse = {
  message: string;
};

export const eventApiClient = {
  index: async (params: IndexRequest = {}): Promise<ApiResult<EventIndexResponse>> => {
    return await axiosClient.get('/tenant/events', { params });
  },
  show: async (id: number): Promise<ApiResult<EventForm>> => {
    return await axiosClient.get(`/tenant/events/${id}`);
  },
  findByEventPreviewUuid: async (uuid: string): Promise<ApiResult<Event>> => {
    return await axiosClient.get(`/tenant/events/preview/${uuid}`);
  },
  create: async (params: {
    event: EventForm;
  }): Promise<ApiResult<EventCreateUpdateResponse, EventCreateErrorResponse>> => {
    return await axiosClient.post('/tenant/events', params);
  },
  update: async (
    id: number,
    params: { event: EventForm },
  ): Promise<ApiResult<EventCreateUpdateResponse, EventUpdateErrorResponse>> => {
    return await axiosClient.put(`/tenant/events/${id}`, params);
  },
  preview: async (
    id: number,
    params: {
      event: EventForm;
    },
  ): Promise<ApiResult<EventPreviewResponse, EventPreviewErrorResponse>> => {
    return await axiosClient.post(`/tenant/events/${id}/preview`, params);
  },
  delete: async (id: number): Promise<ApiResult<unknown>> => {
    return await axiosClient.delete(`/tenant/events/${id}`);
  },
  uploadFile: async (
    file: File,
  ): Promise<ApiResult<EventUploadFileResponse, EventUploadFileErrorResponse>> => {
    const formData = new FormData();
    formData.append('file', file);
    return await axiosClient.post('/tenant/events/upload', formData);
  },
  uploadPdf: async (
    file: File,
  ): Promise<ApiResult<EventUploadFileResponse, EventUploadFileErrorResponse>> => {
    const formData = new FormData();
    formData.append('file', file);
    return await axiosClient.post('/tenant/events/upload/pdf', formData);
  },
  deleteFile: async (
    url: string,
  ): Promise<ApiResult<EventDeleteFileResponse, EventDeleteFileErrorResponse>> => {
    return await axiosClient.delete('/tenant/events/s3delete', {
      params: {
        uploaded_image_url: url,
      },
    });
  },
};
